/**
 * Payer Connections — visual tokens for the five fold states and the four
 * blocked-on parties. Values are from the design handoff
 * (design_handoff_enrollment_dashboard/README.md, "Design tokens" table),
 * which the PRD's fold rule (section 4, AC-3/AC-4) matches exactly.
 *
 * `@jotpsych/ui`'s Chip `soft` variant only exposes MUI's six palette
 * colors, and this palette (five distinct hues incl. a dark "live" chip)
 * doesn't map onto them 1:1 — so state chips apply these hex values via
 * `sx` on top of the shared Chip primitive rather than picking a `color`.
 */

import type { BlockedParty, PayerAudience, PayerState } from './types';

export interface StateTokens {
  label: string;
  bg: string;
  border: string;
  text: string;
}

/** Payer-state chip tokens, including `live` (used on the roster's collapsed count, not on a row). */
export const STATE_TOKENS: Record<PayerState, StateTokens> = {
  needs_us: { label: 'On JotPsych', bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  needs_customer: { label: 'Needs you', bg: '#fffbeb', border: '#fde68a', text: '#b45309' },
  waiting: { label: 'Waiting on payer', bg: '#faf5ff', border: '#e9d5ff', text: '#7e22ce' },
  ready: { label: 'Ready to bill', bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  canceled: { label: 'Canceled', bg: '#f3f4f6', border: '#e5e7eb', text: '#4b5563' },
  live: { label: 'Live', bg: '#111827', border: '#111827', text: '#ffffff' },
};

/** Blocked-on party chip color, using the Chip primitive's palette (AC-14). */
export const PARTY_CHIP_COLOR: Record<BlockedParty, 'error' | 'warning' | 'info' | 'success'> = {
  JOTPSYCH: 'error',
  CUSTOMER: 'warning',
  PAYER: 'info',
  NONE: 'success',
};

/** Row and summary badges share the same audience-specific label. */
export function payerStateLabel(state: PayerState, audience: PayerAudience = 'clinic'): string {
  return state === 'needs_customer' && audience === 'staff'
    ? 'Needs clinic'
    : STATE_TOKENS[state].label;
}
